import { useState } from "react";
import { Phone, Mail } from "lucide-react";
import { ASSETS, CONTACT } from "@/data";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";

const DISCLAIMER_PARAS = [
  "Bij deelname aan een sessie, begeleiding, coaching of andere dienstverlening van Freedom Serenity verklaart de deelnemer kennis te hebben genomen van deze disclaimer en hiermee akkoord te gaan.",
  "Freedom Serenity is gericht op persoonlijke ontwikkeling, bewustwording, ontspanning, innerlijke groei en het versterken van eigen kracht, rust en veerkracht. Onze begeleiding is bedoeld als ondersteuning en inspiratie en vormt geen vervanging voor medische, psychologische, psychiatrische of andere professionele zorg. Er worden geen diagnoses gesteld en er worden geen garanties gegeven over specifieke resultaten.",
  "Iedere persoon bewandelt zijn of haar eigen unieke pad. De deelnemer blijft altijd zelf verantwoordelijk voor de eigen gezondheid, keuzes, grenzen en welzijn, zowel tijdens als na een sessie.",
  "Deelname aan de programma's van Freedom Serenity en het gebruik van onze informatie gebeurt vanuit eigen verantwoordelijkheid. Wij nodigen deelnemers uit om goed naar zichzelf te luisteren, hun eigen grenzen te respecteren en aan te geven wanneer iets niet goed voelt.",
  "Bij lichamelijke of psychische klachten, medische aandoeningen of wanneer iemand onder behandeling is van een zorgprofessional, adviseren wij om waar nodig vooraf overleg te voeren met een bevoegde zorgverlener.",
  "Freedom Serenity kan, voor zover wettelijk toegestaan, niet aansprakelijk worden gesteld voor schade, klachten, lichamelijke of emotionele reacties, verlies of andere gevolgen die voortkomen uit deelname aan onze diensten of het gebruik van onze informatie.",
  "Onze wens is om een veilige, liefdevolle en respectvolle omgeving te creëren waarin iedereen op zijn of haar eigen tempo mag groeien. De ervaring en uitkomst van iedere sessie is persoonlijk en kan per deelnemer verschillen.",
  "Deze disclaimer beperkt geen wettelijke rechten die niet mogen worden uitgesloten.",
];

const Disclaimer = () => (
  <div className="font-body text-sm text-brand-cream/85 leading-relaxed space-y-3 max-h-[60vh] overflow-y-auto pr-2">
    {DISCLAIMER_PARAS.map((p, i) => <p key={i}>{p}</p>)}
  </div>
);

const DisclaimerShort = () => (
  <p className="font-body text-sm text-brand-cream/70 leading-relaxed">
    Freedom Serenity is gericht op persoonlijke ontwikkeling, bewustwording en ontspanning. Onze begeleiding
    is ondersteunend en vormt <strong>geen vervanging voor medische, psychologische of andere professionele
    zorg</strong>. Deelname gebeurt vanuit eigen verantwoordelijkheid. Lees de volledige disclaimer onderaan de pagina.
  </p>
);

const Privacy = () => (
  <p className="font-body text-sm text-brand-ink leading-relaxed">
    Wij respecteren je privacy. De gegevens die je via het contactformulier achterlaat (naam, e-mailadres en
    bericht) gebruiken wij uitsluitend om je vraag te beantwoorden en contact met je op te nemen. We delen je
    gegevens niet met derden en bewaren ze niet langer dan nodig. Wil je dat we je gegevens verwijderen? Mail
    ons via {CONTACT.email}.
  </p>
);

export const Footer = () => {
  const [open, setOpen] = useState(null);
  const year = new Date().getFullYear();

  return (
    <footer className="bg-brand-forestDeep text-brand-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-3 gap-10">
        <div>
          <img src={ASSETS.logo} alt="Freedom Serenity logo" className="h-20 w-auto rounded-xl object-contain ring-1 ring-brand-goldLight/30" />
          <p className="font-script text-brand-goldLight text-4xl mt-2">Thuiskomen in jezelf</p>
        </div>

        <div className="space-y-3">
          <p className="eyebrow !text-brand-goldLight">Contact</p>
          <a href={CONTACT.phoneHref} className="flex items-center gap-2.5 font-body text-brand-cream/85 hover:text-brand-goldLight"><Phone size={15} /> {CONTACT.phone}</a>
          <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 font-body text-brand-cream/85 hover:text-brand-goldLight"><Mail size={15} /> {CONTACT.email}</a>
          <p className="font-body text-brand-cream/70 text-sm">{CONTACT.location}</p>
        </div>

        <div>
          <DisclaimerShort />
        </div>
      </div>

      <div className="border-t border-brand-cream/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-brand-cream/60 text-sm font-body">
          <span>© {year} Freedom Serenity — Sandra & Talicia Arab</span>
          <div className="flex items-center gap-5">
            <Dialog open={open === "privacy"} onOpenChange={(o) => setOpen(o ? "privacy" : null)}>
              <DialogTrigger asChild><button data-testid="footer-privacy" className="hover:text-brand-goldLight">Privacyverklaring</button></DialogTrigger>
              <DialogContent className="bg-brand-paper border-brand-gold/30">
                <DialogHeader><DialogTitle className="font-display text-2xl text-brand-forest">Privacyverklaring</DialogTitle>
                  <DialogDescription asChild><Privacy /></DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>
            <Dialog open={open === "disclaimer"} onOpenChange={(o) => setOpen(o ? "disclaimer" : null)}>
              <DialogTrigger asChild><button data-testid="footer-disclaimer" className="hover:text-brand-goldLight">Disclaimer</button></DialogTrigger>
              <DialogContent className="bg-brand-forestDeep border-brand-goldLight/30 max-w-2xl">
                <DialogHeader><DialogTitle className="font-display text-2xl text-brand-goldLight">Disclaimer en verantwoordelijkheid — Freedom Serenity</DialogTitle>
                  <DialogDescription asChild><Disclaimer /></DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </footer>
  );
};
